import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Video, Clock, Loader2 } from 'lucide-react';
import { formatDateWithWeekday } from '@/lib/utils';
import { attachJitsiLeaveHandler, jitsiConfigOverwrite, jitsiInterfaceConfigOverwrite, loadJitsiApi } from '@/lib/jitsi';

const fmtDT = (iso) => (iso ? formatDateWithWeekday(iso, { weekday: 'long', time: true }) : '—');

export default function PublicConferenceJoin() {
  const { guestToken } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [joined, setJoined] = useState(false);
  const jitsiRef = useRef(null);

  useEffect(() => {
    api.get(`/public/conferences/${guestToken}`)
      .then((r) => {
        setInfo(r.data);
        if (r.data.guest_name) setName(r.data.guest_name);
      })
      .catch((e) => setError(e.response?.data?.detail || 'Einladungslink ungültig'));
  }, [guestToken]);

  useEffect(() => {
    if (!joined || !info) return undefined;
    let disposed = false;

    const init = () => {
      if (disposed || !window.JitsiMeetExternalAPI) return;
      const container = document.getElementById('jitsi-public-container');
      if (!container) return;
      const j = new window.JitsiMeetExternalAPI(info.domain, {
        roomName: info.room,
        parentNode: container,
        width: '100%',
        height: '100%',
        configOverwrite: jitsiConfigOverwrite({
          prejoinPageEnabled: false,
          subject: info.title,
          toolbarButtons: ['microphone', 'camera', 'desktop', 'chat', 'tileview', 'raisehand', 'hangup'],
        }),
        interfaceConfigOverwrite: jitsiInterfaceConfigOverwrite(),
        userInfo: { displayName: name },
      });
      j.addEventListener('videoConferenceJoined', () => {
        j.executeCommand('displayName', name);
        j.executeCommand('subject', info.title);
      });
      attachJitsiLeaveHandler(j, () => {
        jitsiRef.current = null;
        setJoined(false);
      });
      jitsiRef.current = j;
    };

    loadJitsiApi(info.domain).then(init).catch(() => {
      setError('Jitsi-Skript konnte nicht geladen werden');
    });
    return () => {
      disposed = true;
      try { jitsiRef.current?.dispose(); } catch { /* noop */ }
    };
  }, [joined, info, name]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <Video className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <h1 className="text-lg font-bold text-slate-900 mb-1">Konferenz nicht verfügbar</h1>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  if (joined) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col">
        <div className="px-4 py-2 text-white text-sm font-semibold flex items-center gap-2">
          <Video className="h-4 w-4 text-brand-red/70" /> {info.title}
        </div>
        <div id="jitsi-public-container" data-testid="jitsi-public-container" className="flex-1" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full" data-testid="public-conference-lobby">
        <div className="flex items-center gap-2 text-brand-red text-xs font-bold uppercase tracking-wider mb-3">
          <Video className="h-4 w-4" /> Onlinekonferenz
        </div>
        <h1 className="text-2xl font-bold text-slate-900">{info.title}</h1>
        <p className="text-sm text-slate-500 mt-2 flex items-center gap-1.5">
          <Clock className="h-4 w-4" /> {fmtDT(info.scheduled_at)} · ca. {info.duration_minutes} Min
        </p>
        {info.description && <p className="text-sm text-slate-600 mt-3">{info.description}</p>}
        <div className="mt-6">
          <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Ihr Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Vor- und Nachname"
                 className="mt-1" data-testid="public-conference-name" autoFocus
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (name.trim()) setJoined(true); } }} />
        </div>
        <Button onClick={() => name.trim() ? setJoined(true) : null} disabled={!name.trim()}
                className="w-full mt-4 bg-brand-red hover:bg-brand-red/90 h-11 text-base"
                data-testid="public-conference-join-btn">
          <Video className="h-5 w-5 mr-2" /> Jetzt beitreten
        </Button>
        <p className="text-[11px] text-slate-400 mt-4 text-center">
          Keine Anmeldung erforderlich. Mit dem Beitritt akzeptieren Sie die Übertragung von Bild und Ton über Jitsi Meet.
        </p>
      </div>
    </div>
  );
}
